import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { startAnalytics } from './lib/analytics';
import { queryClient } from './lib/api';
import { AppShell } from './components/Layout';
import { Toaster } from './components/ui';
import { AcceptInvitePage, ForgotPasswordPage, LoginPage, ResetPasswordPage, SignupPage, VerifyEmailPage } from './pages/Login';
import { ConnectPage } from './pages/Connect';
import { CommandCenterPage } from './pages/CommandCenter';
import { ApprovalsPage } from './pages/Approvals';
import { JournalPage, JournalEntryPage, NewJournalEntryPage } from './pages/Journal';
import { AccountLedgerPage, AccountsPage } from './pages/Accounts';
import { ClosePage } from './pages/Close';
import { CustomerPage, CustomersPage } from './pages/Customers';
import { ContractPage, ContractsPage, NewContractPage } from './pages/Contracts';
import { InvoicePage, InvoicesPage, NewInvoicePage } from './pages/Invoices';
import { VendorPage, VendorsPage } from './pages/Vendors';
import { BillPage, BillsPage, NewBillPage } from './pages/Bills';
import { CashPage } from './pages/Cash';
import { ProductsPage } from './pages/Products';
import { DashboardPage, DashboardsPage } from './pages/Dashboards';
import { ReportPage, ReportsPage } from './pages/Reports';
import { ControlsPage } from './pages/Controls';
import { AssistantPage } from './pages/Assistant';
import { SettingsPage } from './pages/Settings';
import { AdminPage } from './pages/Admin';
import { PricingPage } from './site/Pricing';
import { ContactPage, DpaPage, PrivacyPage, RefundsPage, TermsPage } from './site/Legal';
import { ArticlePage } from './site/Article';
import { COMPARE_PATHS, GUIDE_PATHS } from './site/articles';
import { sessionHint } from './site/hooks';

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  // An AI app asking to connect with OAuth (apps/server/src/routes/oauth.ts): sign in, choose the company and role.
  { path: '/connect', element: <ConnectPage /> },
  // The public site. Signed-out visitors to `/` get the landing page from AppShell.
  { path: '/pricing', element: <PricingPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/refunds', element: <RefundsPage /> },
  { path: '/dpa', element: <DpaPage /> },
  { path: '/contact', element: <ContactPage /> },
  ...[...COMPARE_PATHS, ...GUIDE_PATHS, '/about' as const].map((p) => ({ path: p, element: <ArticlePage key={p} path={p} /> })),
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <CommandCenterPage /> },
      { path: 'approvals', element: <ApprovalsPage /> },
      // One optional-segment route so the page keeps its state (and the live stream) when a new thread gets its id.
      { path: 'assistant/:threadId?', element: <AssistantPage /> },
      { path: 'journal', element: <JournalPage /> },
      { path: 'journal/new', element: <NewJournalEntryPage /> },
      { path: 'journal/:id', element: <JournalEntryPage /> },
      { path: 'accounts', element: <AccountsPage /> },
      { path: 'accounts/:id', element: <AccountLedgerPage /> },
      { path: 'close', element: <ClosePage /> },
      { path: 'ar', element: <Navigate to="/ar/customers" replace /> },
      { path: 'ar/customers', element: <CustomersPage /> },
      { path: 'ar/customers/:id', element: <CustomerPage /> },
      { path: 'ar/products', element: <ProductsPage /> },
      { path: 'ar/contracts', element: <ContractsPage /> },
      { path: 'ar/contracts/new', element: <NewContractPage /> },
      { path: 'ar/contracts/:id', element: <ContractPage /> },
      { path: 'ar/invoices', element: <InvoicesPage /> },
      { path: 'ar/invoices/new', element: <NewInvoicePage /> },
      { path: 'ar/invoices/:id', element: <InvoicePage /> },
      { path: 'ap', element: <Navigate to="/ap/bills" replace /> },
      { path: 'ap/vendors', element: <VendorsPage /> },
      { path: 'ap/vendors/:id', element: <VendorPage /> },
      { path: 'ap/bills', element: <BillsPage /> },
      { path: 'ap/bills/new', element: <NewBillPage /> },
      { path: 'ap/bills/:id', element: <BillPage /> },
      { path: 'cash', element: <CashPage /> },
      { path: 'dashboards', element: <DashboardsPage /> },
      { path: 'dashboards/:id', element: <DashboardPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'reports/:name', element: <ReportPage /> },
      { path: 'controls', element: <ControlsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      // The owner's dashboard: site and app usage, sign-ups, the AI review (ADMIN_EMAILS on the server).
      { path: 'admin', element: <AdminPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

function NotFound() {
  return (
    <div className="py-24 text-center">
      <div className="text-[15px] font-semibold">Page not found</div>
      <a href="/" className="mt-2 inline-block text-[13px] text-brand hover:underline">
        Back to the command center
      </a>
    </div>
  );
}

// The server renders the public pages (ssr.tsx) and sends the data it rendered them from, so the first render here
// matches its HTML and takes it over instead of drawing the page again. Other pages arrive empty.
const root = document.getElementById('root')!;
const data = document.getElementById('rm-data')?.textContent;
if (data) {
  const { site, plan } = JSON.parse(data) as { site: unknown; plan: unknown };
  queryClient.setQueryData(['/api/site'], site);
  queryClient.setQueryData(['/api/billing/plan'], plan);
}
const app = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  </StrictMode>
);
// The server sends the landing page at `/` only to a browser without a session, so a signed-in hint is stale there.
if (root.firstElementChild && location.pathname === '/') sessionHint.set(false);
// After that hint, which decides whether a page counts as the public site or the app.
startAnalytics(router);
if (root.firstElementChild) hydrateRoot(root, app);
else createRoot(root).render(app);
